import { useState } from 'react';
import { useMatch } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { domainCodeAtom, useSaladBar } from '@australiangreens/ag-internal-components';
import { useRedisWritesBlocked } from '@/apis/call';
import {
  QFilter,
  TASK_CHOICE_MAPPING_REVERSE,
  TASK_KIND_MAPPING_REVERSE,
} from '@/types';
import { useDownloadPdf } from '@components/DistributionListPanel';
import { findUpdatedRocketStatusCharacter } from '@components/OtherActionsPanelContent/shared/findUpdatedRocketStatusCharacter';
import type { OtherActionsPanelFormState } from '@components/OtherActionsPanelContent/shared/useOtherActionsPanelForm';
import {
  useAddContactsToGroups,
  useAddTagsToContacts,
  useCreateGroupsWithContacts,
  useRemoveContactsFromGroups,
  useRemoveTagsFromContacts,
  useSetRocketStatusForContact,
  useUpdatePathwayStepsForQFilter,
  useUpdateTasksForContacts,
} from '@components/OtherActionsPanelContent/hooks';
import { useDeleteContactsFromList } from '@components/OtherActionsPanelContent/shared/useDeleteContactsFromList';

const getIdsMapper = <T,>({ id }: { id: T }) => id;

export function useOtherActionsPanelQFilterApply({
  form,
  qFilter,
  listId,
  sortOrder,
  sortColumn,
  onClose,
}: {
  form: OtherActionsPanelFormState;
  qFilter: QFilter;
  listId: number;
  sortOrder: string;
  sortColumn: string;
  onClose: (isSuccessful: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const domainCode = useAtomValue(domainCodeAtom);
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const match = useMatch('/lists/:listId/*');
  const [applyLoading, setApplyLoading] = useState(false);

  const downloadPdf = useDownloadPdf(
    form.distributionListElection.id,
    String(match?.params.listId ?? ''),
    sortOrder,
    sortColumn,
    form.distributionListNotes
  );

  const { mutateAsync: deleteContactsFromList } = useDeleteContactsFromList(listId);
  const { mutateAsync: addContactsToGroups } = useAddContactsToGroups();
  const { mutateAsync: removeContactsFromGroups } = useRemoveContactsFromGroups();
  const { mutateAsync: createGroups } = useCreateGroupsWithContacts();
  const { mutateAsync: addTagsToContacts } = useAddTagsToContacts();
  const { mutateAsync: removeTagsFromContacts } = useRemoveTagsFromContacts();
  const { mutateAsync: setRocketStatus } = useSetRocketStatusForContact();
  const { mutateAsync: updateTasks } = useUpdateTasksForContacts();
  const { mutateAsync: updatePathways } = useUpdatePathwayStepsForQFilter();

  const handleApply = async () => {
    if (writesBlocked || !form.isAtLeastOneFieldSet) return;

    const groupIdsToAdd = form.groupAddEntities.map(getIdsMapper);
    const groupIdsToRemove = form.groupRemoveEntities.map(getIdsMapper);
    const groupNamesToCreate = form.newGroupNames.map(({ label }) => label);
    const tagIdsToAdd = form.tagAddEntities.map(getIdsMapper);
    const tagIdsToRemove = form.tagRemoveEntities.map(getIdsMapper);
    const electionId = form.electionValue ? form.electionValue.id : null;
    const taskKindTypeValue = form.taskKindType
      ? TASK_KIND_MAPPING_REVERSE[form.taskKindType]
      : null;
    const levelOfInterestValue = form.levelOfInterest
      ? TASK_CHOICE_MAPPING_REVERSE[form.levelOfInterest]
      : null;
    const updatePathwayStepId = form.updatePathwayStep ? form.updatePathwayStep.id : null;
    const updatePathwayOptionId = form.updatePathwayOption ? form.updatePathwayOption.id : null;

    setApplyLoading(true);
    const actions: Promise<unknown>[] = [];

    if (form.isDownloadPdfChecked) {
      actions.push(downloadPdf(qFilter.contactIds));
    }
    if (groupIdsToAdd.length) {
      actions.push(addContactsToGroups({ groupIds: groupIdsToAdd, qFilter }));
    }
    if (groupIdsToRemove.length) {
      actions.push(removeContactsFromGroups({ groupIds: groupIdsToRemove, qFilter }));
    }
    if (groupNamesToCreate.length) {
      actions.push(createGroups({ groupNames: groupNamesToCreate, qFilter }));
    }
    if (tagIdsToAdd.length) {
      actions.push(addTagsToContacts({ tagIds: tagIdsToAdd, qFilter }));
    }
    if (tagIdsToRemove.length) {
      actions.push(removeTagsFromContacts({ tagIds: tagIdsToRemove, qFilter }));
    }
    if (form.updateRocketStatus !== null) {
      const rocketStatusAbbrev =
        findUpdatedRocketStatusCharacter(form.updateRocketStatus) ?? '';
      actions.push(setRocketStatus({ status: rocketStatusAbbrev, qFilter }));
    }
    if (electionId !== null && taskKindTypeValue !== null && levelOfInterestValue !== null) {
      actions.push(
        updateTasks({
          qFilter,
          electionId,
          taskKindType: taskKindTypeValue,
          levelOfInterest: levelOfInterestValue,
          notes: form.notes,
          domainCode,
        })
      );
    }
    if (updatePathwayStepId !== null && updatePathwayOptionId !== null) {
      actions.push(
        updatePathways({
          qFilter,
          domainCode,
          stepId: updatePathwayStepId,
          optionId: updatePathwayOptionId,
        })
      );
    }
    let failed = false;
    try {
      await Promise.all(actions);
      if (form.canDeleteFromList && qFilter.contactIds && qFilter.contactIds.length > 0) {
        await deleteContactsFromList(qFilter.contactIds);
      }
    } catch {
      failed = true;
      enqueueErrorNotification('Actions could not be applied.');
    } finally {
      setApplyLoading(false);
    }
    if (failed) return;

    queryClient.invalidateQueries({ queryKey: ['preview'] });
    if (listId) queryClient.invalidateQueries({ queryKey: ['list', listId] });
    form.resetInternalState();
    onClose(true);
  };

  return { applyLoading, handleApply };
}
